<template>
  <div class="image" :class="{ skeleton: skeleton && !loaded }">
    <picture :key="url">
      <template v-for="source in sources" :key="`${source.breakpoint}-${source.type}`">
        <source v-if="lazy" :type="source.type" :data-srcset="source.source" :media="`(min-width: ${source.breakpoint}px)`" />
        <source v-else :type="source.type" :srcset="source.source" :media="`(min-width: ${source.breakpoint}px)`" />
      </template>
      <img
        v-if="lazy"
        :data-srcset="defaultSrcset"
        :data-src="defaultUrl"
        class="lazyload"
        :alt="alt"
        :width="width && height ? width : null"
        :height="width && height ? height : null"
        @load="onImgLoad"
      />
      <img
        v-else
        :srcset="defaultSrcset"
        :src="defaultUrl"
        :alt="alt"
        :width="width && height ? width : null"
        :height="width && height ? height : null"
        @load="onImgLoad"
      />
    </picture>
  </div>
</template>

<script>
import { buildUrl } from '@/components/commons/utils.js';
import { computed, ref } from 'vue';

const WEBP_TYPE = 'image/webp';
const JPG_TYPE = '';

export default {
  props: {
    lazy: {
      default: true,
      type: Boolean,
    },
    url: {
      default: null,
      type: String,
    },
    widths: {
      type: Array,
      default: () => [320, 425, 768, 970, 1300, 1920],
    },
    breakpoints: {
      type: Array,
      default: () => [320, 425, 768, 970, 1300, 1920],
    },
    densities: {
      default: () => [1, 2],
      type: Array,
    },
    types: {
      default: () => [WEBP_TYPE, JPG_TYPE],
      type: Array,
    },
    alt: {
      default: '',
      type: String,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Number,
      default: null,
    },
    height: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const loaded = ref(false);
    const reversedWidths = computed(() => [...props.widths].reverse());
    const reversedBreakpoints = computed(() => [...props.breakpoints].reverse());

    const sources = computed(() =>
      reversedBreakpoints.value.flatMap((breakpoint, index) =>
        props.types.map((type) => ({
          type,
          breakpoint,
          source: getSrcset(reversedWidths.value[index], type),
        })),
      ),
    );

    const defaultSrcset = computed(() => `${formatUrl(props.widths[0], 1)} 1x, ${formatUrl(props.widths[0], 2)} 2x`);

    const defaultUrl = computed(() => formatUrl(props.widths[0], 1));

    function getQuality(width, density) {
      if (density > 1) {
        if (width <= 767) {
          return 40;
        }
        return 60;
      }
      return 80;
    }

    function formatUrl(width, density, type) {
      if (!props.url) {
        return null;
      }
      const isWebp = type === WEBP_TYPE;
      const quality = getQuality(width, density);
      const replacedUrl = props.url.replace('{width}', Math.ceil(width * density)).replace('{q}', quality);
      return isWebp ? buildUrl(replacedUrl, { fm: 'webp' }) : replacedUrl;
    }

    function getSrcset(width, type) {
      return props.densities.map((density) => `${formatUrl(width, density, type)} ${density}x`).join(', ');
    }

    if (import.meta.env.SSR) {
      loaded.value = true;
    }

    return {
      JPG_TYPE,
      WEBP_TYPE,
      loaded,
      sources,
      defaultSrcset,
      defaultUrl,
    };
  },
  methods: {
    onImgLoad() {
      this.$emit('imageLoaded');
      this.loaded = true;
    },
  },
};
</script>

<style lang="postcss" scoped>
.image {
  .lazyload,
  .lazyloading {
    opacity: 0;
  }
  .lazyloaded {
    opacity: 1;
    transition: opacity 100ms;
  }

  img {
    width: 100%;
  }

  img.lazyloaded[src*="about:blank"] {
	  visibility: hidden;
  }
}


</style>
